import { useState, useMemo, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/useAuth';
import type { Supplier } from '../types';

// Extended supplier interface with category and trade names
interface SupplierWithDetails extends Supplier {
  categoryName?: string;
  tradeNames?: string[];
}

export const useSupplierData = () => {
  const { profile } = useAuth() as any;
  const companyId = profile?.company_id ?? null;
  const [suppliers, setSuppliers] = useState<SupplierWithDetails[]>([]);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [loading, setLoading] = useState(true);

  const fetchSuppliers = useCallback(async () => {
    if (!companyId) {
      setSuppliers([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      
      // Fetch suppliers with basic data
      const { data: suppliersData, error } = await supabase
        .from('suppliers')
        .select('*')
        .eq('company_id', companyId)
        .order(sortBy, { ascending: sortDir === 'asc' });

      if (error) {
        console.error('Error fetching suppliers:', error);
        return;
      }

      // If no suppliers, set empty array
      if (!suppliersData || suppliersData.length === 0) {
        setSuppliers([]);
        return;
      }

      // Fetch categories and trades for mapping
      const [categoriesRes, tradesRes] = await Promise.all([
        supabase.from('supplier_categories').select('id, name'),
        supabase.from('supplier_trades').select('id, name')
      ]);

      const categoriesMap = new Map(
        (categoriesRes.data || []).map(cat => [cat.id, cat.name])
      );
      const tradesMap = new Map(
        (tradesRes.data || []).map(trade => [trade.id, trade.name])
      );

      // Map suppliers with category and trade names
      const enrichedSuppliers: SupplierWithDetails[] = suppliersData.map(supplier => {
        const categoryName = supplier.category_id ? categoriesMap.get(supplier.category_id) : undefined;
        const tradeNames = supplier.trade_ids 
          ? supplier.trade_ids.map(id => tradesMap.get(id)).filter(Boolean)
          : [];

        return {
          ...supplier,
          categoryName,
          tradeNames
        };
      });

      setSuppliers(enrichedSuppliers);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortDir, companyId]);

  // Apply search to suppliers
  const searchedSuppliers = useMemo(() => {
    if (!search.trim()) return suppliers;

    const searchTerm = search.toLowerCase();
    return suppliers.filter(supplier => {
      return (
        supplier.name?.toLowerCase().includes(searchTerm) ||
        supplier.contact_name?.toLowerCase().includes(searchTerm) ||
        supplier.email?.toLowerCase().includes(searchTerm) ||
        supplier.phone?.toLowerCase().includes(searchTerm) ||
        supplier.street_address?.toLowerCase().includes(searchTerm) ||
        supplier.suburb?.toLowerCase().includes(searchTerm) ||
        supplier.city?.toLowerCase().includes(searchTerm) ||
        supplier.categoryName?.toLowerCase().includes(searchTerm) ||
        supplier.tradeNames?.some(trade => trade.toLowerCase().includes(searchTerm))
      );
    });
  }, [suppliers, search]);

  // Initial fetch and re-fetch when sort parameters change
  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  return {
    suppliers: searchedSuppliers,
    fetchSuppliers,
    search,
    setSearch,
    sortBy,
    setSortBy,
    sortDir,
    setSortDir,
    loading,
  };
};